import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchSRA, checkSRASearchStatus, getUserAiCredits } from '../../services/sraSearchService';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchState } from '../../contexts/SearchStateContext';
import { FaSearch, FaRobot, FaExclamationTriangle, FaLock, FaCoins } from 'react-icons/fa';
import { useToast } from '../common/Toast';
import SearchHistorySection from './SearchHistorySection';
import '../../styles/components/_search-history-section.scss';

const AiSearchBar = () => {
  const { searchState, saveAiSearchState } = useSearchState();
  
  // Initialize query from preserved state or localStorage
  const getInitialQuery = () => {
    // First check preserved search state
    if (searchState?.searchType === 'ai' && searchState?.aiSearchCriteria?.query) {
      return searchState.aiSearchCriteria.query;
    }
    // Fallback to localStorage
    return localStorage.getItem('zahoot_ai_search_query') || '';
  };
  
  const [query, setQuery] = useState(getInitialQuery());
  const [isLoading, setIsLoading] = useState(false);
  const [isAiAvailable, setIsAiAvailable] = useState(true);
  const [aiError, setAiError] = useState(null);
  const [placeholder, setPlaceholder] = useState('Ask about solicitors in natural language...');
  const [userCredits, setUserCredits] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();

  // Check user's credit status when authenticated
  useEffect(() => {
    const checkUserCredits = async () => {
      if (!user) {
        setUserCredits(null);
        return;
      }
      
      setCreditsLoading(true);
      try {
        const credits = await getUserAiCredits();
        setUserCredits(credits);
      } catch (error) {
        console.error('Error fetching user credits:', error);
        // Don't set error state here as credits might still work
      } finally {
        setCreditsLoading(false);
      }
    };
    
    checkUserCredits();
  }, [user]);

  // Listen for credit updates from AI search
  useEffect(() => {
    const handleCreditsUpdate = (event) => {
      console.log('AiSearchBar received credit update:', event.detail);
      setUserCredits(event.detail);
    };

    window.addEventListener('aiCreditsUpdated', handleCreditsUpdate);
    return () => window.removeEventListener('aiCreditsUpdated', handleCreditsUpdate);
  }, []);

  // Check if AI search is available when component mounts
  useEffect(() => {
    const checkAiStatus = async () => {
      try {
        const isAvailable = await checkSRASearchStatus();
        setIsAiAvailable(isAvailable);
        
        if (!isAvailable) {
          setPlaceholder('Standard search available');
          setAiError('AI search temporarily unavailable');
        }
      } catch (error) {
        console.error('Error checking AI search status:', error);
        setIsAiAvailable(false);
        setPlaceholder('Standard search available');
        setAiError('AI search temporarily unavailable');
      }
    };
    
    checkAiStatus();
  }, []);

  // Example queries for the placeholder
  useEffect(() => {
    if (!isAiAvailable || aiError || !user || (userCredits && userCredits.remaining <= 0)) return;
    
    const examples = [
      'Find me divorce solicitors in London with 4+ star reviews',
      'Who are the best property solicitors in Manchester?',
      'Show solicitors specialising in immigration with good ratings',
      'Find me employment solicitors who can help with discrimination cases',
      'Which solicitors near Birmingham handle medical negligence?'
    ];
    
    let currentIndex = 0;
    const interval = setInterval(() => {
      setPlaceholder(examples[currentIndex]);
      currentIndex = (currentIndex + 1) % examples.length;
    }, 3000);
    
    return () => clearInterval(interval);
  }, [isAiAvailable, aiError, user, userCredits]);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!query.trim()) return;
    
    // Check if user is authenticated
    if (!user) {
      // Redirect to signup with AI search intent and return URL
      const returnUrl = `/search?ai=true&q=${encodeURIComponent(query)}`;
      navigate(`/signup?feature=ai-search&returnTo=${encodeURIComponent(returnUrl)}`);
      return;
    }
    
    // Check if user has credits
    if (userCredits && userCredits.remaining <= 0) {
      // Show error message instead of redirecting
      setAiError('No AI search credits remaining');
      showToast('You have no AI search credits remaining. Please upgrade your account or use standard search.', 'warning');
      return;
    }
    
    setIsLoading(true);
    setAiError(null);
    
    try {
      // Save AI search state before navigation
      if (isAiAvailable && !aiError) {
        saveAiSearchState(query, query);
        localStorage.setItem('zahoot_ai_search_query', query);
        navigate(`/search?ai=true&q=${encodeURIComponent(query)}`);
      } else {
        // Use standard search
        navigate(`/search?q=${encodeURIComponent(query)}`);
      }
    } catch (error) {
      console.error('Search error:', error);
      setAiError('Search failed - please try again');
      navigate(`/search?q=${encodeURIComponent(query)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getIcon = () => {
    if (!user) return <FaLock className="text-white text-xl" />;
    if (userCredits && userCredits.remaining <= 0) return <FaCoins className="text-white text-xl" />;
    if (aiError) return <FaExclamationTriangle className="text-white text-xl" />;
    return <FaRobot className="text-white text-xl" />;
  };

  const getPlaceholder = () => {
    if (!user) return 'Sign up to unlock AI search...';
    if (userCredits && userCredits.remaining <= 0) return 'No AI credits remaining - standard search available...';
    if (aiError) return 'Search using standard search...';
    return placeholder;
  };

  const getIconBgColor = () => {
    if (!user) return 'bg-gray-500';
    if (userCredits && userCredits.remaining <= 0) return 'bg-orange-500';
    if (isAiAvailable && !aiError) return 'bg-purple-600';
    return 'bg-gray-500';
  };

  const getStatusMessage = () => {
    if (!user) {
      return (
        <div className="px-4 py-2 text-sm text-white/90">
          <span className="font-semibold">🎉 Sign up for 3 free AI searches</span> to find the perfect solicitor!
        </div>
      );
    }
    
    if (creditsLoading) {
      return (
        <div className="px-4 py-2 text-sm text-gray-300">
          <span className="font-semibold">Loading credits...</span>
        </div>
      );
    }
    
    if (userCredits && userCredits.remaining <= 0) {
      return (
        <div className="px-4 py-2 text-sm text-orange-300">
          <span className="font-semibold">⚡ No AI credits remaining</span> - Standard search is available. Upgrade for more AI searches!
        </div>
      );
    }
    
    if (userCredits && userCredits.remaining > 0) {
      return (
        <div className="px-4 py-2 text-sm text-green-300">
          <span className="font-semibold">⚡ {userCredits.remaining} AI searches remaining</span> - Find solicitors with natural language!
        </div>
      );
    }
    
    if (aiError) {
      return (
        <div className="px-4 py-2 text-sm text-yellow-300">
          <span className="font-semibold">Notice:</span> {aiError}. Standard search is available.
        </div>
      );
    }
    
    if (isAiAvailable) {
      return (
        <div className="px-4 py-2 text-sm text-gray-300">
                        <span className="font-semibold">Examples:</span> "Divorce solicitors in London", "Property solicitors with 5-star ratings"
        </div>
      );
    }
    
    return (
      <div className="px-4 py-2 text-sm text-gray-300">
        <span className="font-semibold">Standard search available:</span> Search by service type and location
      </div>
    );
  };

  return (
    <div className="ai-search-bar-container w-full max-w-4xl mx-auto">
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-2 shadow-lg border border-white/20">
        <form onSubmit={handleSearch} className="flex items-center">
          <div className={`flex items-center justify-center w-12 h-12 rounded-full ${getIconBgColor()}`}>
            {getIcon()}
          </div>
          
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={getPlaceholder()}
            className="flex-1 py-3 px-4 bg-transparent text-white placeholder-gray-300 focus:outline-none text-lg"
          />
          
          <button
            type="submit"
            disabled={isLoading}
            className={`flex items-center justify-center w-12 h-12 rounded-full ${isLoading ? 'bg-gray-500' : 'bg-gradient-to-r from-[#D80364] to-[#F0654B]'} text-white`}
          >
            {isLoading ? (
              <div className="spinner w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <FaSearch className="text-white text-xl" />
            )}
          </button>
        </form>
        
        {/* Show appropriate message based on authentication, credits, and AI availability */}
        {getStatusMessage()}
      </div>
      
      {/* Search History Section - Only show for authenticated users */}
      <SearchHistorySection />
    </div>
  );
};

export default AiSearchBar; 